import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle } from 'lucide-react';

/**
 * Full-width rejection callout for document / request cards.
 *
 * Short reasons render in full. Long reasons clamp to two lines with a
 * Show more control — list cards stay scannable without hiding the instruction.
 *
 * @param {string} reason
 * @param {string} [label]
 * @param {boolean} [collapsed] When true, clamp long copy until expanded.
 */
export const RejectionFeedback = ({
  reason,
  label = 'Rejection reason',
  collapsed = true,
}) => {
  const text = (reason || '').trim();
  const textRef = useRef(null);
  const [expanded, setExpanded] = useState(!collapsed);
  const [canToggle, setCanToggle] = useState(false);

  useEffect(() => {
    setExpanded(!collapsed);
    setCanToggle(false);
  }, [collapsed, text]);

  useEffect(() => {
    if (expanded) return;
    const el = textRef.current;
    if (!el) return;
    setCanToggle(el.scrollHeight > el.clientHeight + 1);
  }, [expanded, text]);

  if (!text) return null;

  return (
    <div className="rounded-xl p-3 bg-red-50 border border-red-200 flex items-start gap-2">
      <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-red-800 uppercase tracking-wide">{label}</p>
        <p
          ref={textRef}
          className={`text-sm text-red-700 mt-0.5 break-words ${expanded ? '' : 'line-clamp-2'}`}
        >
          {text}
        </p>
        {canToggle && (
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            className="mt-1 text-xs font-medium text-red-800 hover:text-red-900 rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 focus-visible:ring-offset-red-50"
            aria-expanded={expanded}
          >
            {expanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>
    </div>
  );
};

export default RejectionFeedback;
